import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  Award,
  ArrowLeft,
  Download,
  Info,
  Loader2,
  RefreshCw,
  Sparkles,
  UserCheck,
  ShieldAlert,
  Target,
  BookOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getMyReport, regenerateMyReport } from "@/lib/visa-readiness/reports.functions";
import { requestExpertReview } from "@/lib/visa-readiness/expert-reviews.functions";
import type { AiAnalysis, ScoreBreakdown, PartialForm } from "@/lib/visa-readiness/types";
import { site } from "@/lib/site";

export const Route = createFileRoute("/visa-readiness/report/$id")({
  head: () => ({
    meta: [
      { title: "Your Visa Readiness Report" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ReportPage,
});

// ---------- score helpers ----------
type Tone = "green" | "yellow" | "red";
function toneFor(score: number): Tone {
  if (score >= 75) return "green";
  if (score >= 50) return "yellow";
  return "red";
}
const TONE_BAR: Record<Tone, string> = {
  green: "bg-[#16a34a]",
  yellow: "bg-[#ca8a04]",
  red: "bg-[#dc2626]",
};
const TONE_TEXT: Record<Tone, string> = {
  green: "text-[#15803d]",
  yellow: "text-[#a16207]",
  red: "text-[#b91c1c]",
};
const TONE_BADGE_BG: Record<Tone, string> = {
  green: "bg-[#dcfce7]",
  yellow: "bg-[#fef9c3]",
  red: "bg-[#fee2e2]",
};

// Metric definitions (label + tooltip)
const METRICS: { key: keyof ScoreBreakdown; label: string; tooltip: string }[] = [
  {
    key: "academic",
    label: "Academic",
    tooltip: "Driven by your GPA, backlogs, degree level, English test scores, and study gaps.",
  },
  {
    key: "financial",
    label: "Financial",
    tooltip: "Based on first-year cost vs. sponsor income, loan amount, liquid funds and ITR clarity.",
  },
  {
    key: "career",
    label: "Career Depth",
    tooltip: "Reflects internships, full-time months, and depth of the projects narrative.",
  },
  {
    key: "consistency",
    label: "DS-160 Consistency",
    tooltip:
      "Checks whether your DS-160 income, work history and prior US travel entries match the rest of your profile.",
  },
  {
    key: "documentation",
    label: "Documentation",
    tooltip:
      "Field-completeness of your \"why this university\", post-graduation plan and home-country ties answers.",
  },
];

function ReportPage() {
  const { id } = useParams({ from: "/visa-readiness/report/$id" });
  const [report, setReport] = useState<Awaited<ReturnType<typeof fetchReport>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<false | "regen" | "review">(false);

  const fetchReport = useServerFn(getMyReport);
  const regen = useServerFn(regenerateMyReport);
  const requestReview = useServerFn(requestExpertReview);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetchReport({ data: { id } });
        setReport(r);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Could not load report");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, fetchReport]);

  const handleRegen = async () => {
    if (!report) return;
    setBusy("regen");
    try {
      const res = await regen({ data: { assessmentId: report.assessmentId } });
      window.location.href = `/visa-readiness/report/${res.reportId}`;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not regenerate");
      setBusy(false);
    }
  };

  const handleReview = async () => {
    if (!report) return;
    setBusy("review");
    try {
      await requestReview({ data: { assessmentId: report.assessmentId } });
      toast.success("Expert review requested. A Specialist will respond within 48 hours.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not request review");
    } finally {
      setBusy(false);
    }
  };

  const form = (report?.formData ?? {}) as PartialForm;
  const refusalCount = useMemo(() => {
    if (!form.s7?.previousUsRefusal) return 0;
    const band = form.s7.refusalCountBand;
    if (band === "more_than_1") return 2;
    if (band === "1") return 1;
    return Number(form.s7.refusalCount ?? 1) || 1;
  }, [form.s7]);
  const isRefused = refusalCount > 0;

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!report) {
    return (
      <div className="mx-auto max-w-md p-10 text-center">
        <p>Report not found.</p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/student-dashboard">Back to dashboard</Link>
        </Button>
      </div>
    );
  }

  const scores = report.scores as ScoreBreakdown;
  const ai = report.aiAnalysis as AiAnalysis;
  const approvalProbability = Math.max(0, Math.min(100, scores.risk));
  const approvalTone = toneFor(approvalProbability);
  const riskLabel = ai.riskAssessment.twoFourteenBRisk;
  const documentId = report.id.slice(0, 8).toUpperCase();
  const targetUniversity = String(form.s4?.universityName ?? "-");
  const targetConsulate = String(form.s1?.targetConsulate ?? "-");
  const applicantName = String(form.s1?.fullName ?? "").trim() || "Applicant";

  return (
    <TooltipProvider delayDuration={100}>
      <div className="bg-background print:bg-white">
        {/* Print-only formal header */}
        <div className="print-only report-doc mb-6 border-b-2 border-gray-900 pb-3">
          <div className="flex items-start justify-between gap-6">
            <div>
              <p className="text-lg font-bold tracking-tight">WeLivdIt</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-600">Confidential Profile Assessment</p>
            </div>
            <div className="text-right">
              <p className="text-[11px] font-semibold uppercase tracking-widest">Official Profile Assessment Report</p>
              <p className="mt-1 text-[10px] text-gray-600">
                Doc ID: {documentId} · Issued {new Date(report.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3 text-[11px]">
            <div><span className="text-gray-500">Applicant:</span> <b>{applicantName}</b></div>
            <div><span className="text-gray-500">University:</span> <b>{targetUniversity}</b></div>
            <div><span className="text-gray-500">Consulate:</span> <b>{targetConsulate}</b></div>
          </div>
        </div>

        <div className="report-doc mx-auto max-w-3xl px-5 py-10 print:py-0">
          {/* Toolbar (screen only) */}
          <div className="no-print mb-4 flex flex-wrap items-center justify-between gap-3">
            <Button asChild variant="ghost" size="sm">
              <Link to="/student-dashboard"><ArrowLeft className="mr-1 h-4 w-4" /> Back</Link>
            </Button>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => window.print()}>
                <Download className="mr-1 h-4 w-4" /> Save as PDF
              </Button>
              <Button size="sm" variant="outline" onClick={handleRegen} disabled={busy !== false}>
                {busy === "regen" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />}
                Regenerate
              </Button>
              <Button size="sm" onClick={handleReview} disabled={busy !== false}>
                {busy === "review" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <UserCheck className="mr-1 h-4 w-4" />}
                Request Specialist review
              </Button>
            </div>
          </div>

          {/* Overall score */}
          <div className="report-card rounded-2xl border bg-card p-6 print-avoid-break">
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary no-print">
              <Sparkles className="h-3 w-3" /> AI Report
            </span>
            <h1 className="mt-3 font-display text-3xl font-semibold">Your Visa Readiness Report</h1>
            <p className="text-sm text-muted-foreground">Generated {new Date(report.createdAt).toLocaleString()}</p>
            <div className="mt-6 flex items-baseline gap-3">
              <Award className="h-8 w-8 text-primary" />
              <span className="font-display text-5xl font-bold">{scores.overall}</span>
              <span className="text-lg text-muted-foreground">/ 100</span>
            </div>
            <Progress value={scores.overall} className="mt-2 h-2" />
            <p className="mt-4 text-sm text-foreground/80">{ai.executiveSummary}</p>
          </div>

          {/* Score breakdown with tooltips + color-coded bars */}
          <div className="report-card mt-4 rounded-2xl border bg-card p-6 print-avoid-break">
            <h2 className="font-display text-xl font-semibold">Score breakdown</h2>
            <div className="mt-4 space-y-3">
              {METRICS.map(({ key, label, tooltip }) => {
                const val = scores[key] as number;
                const tone = toneFor(val);
                return (
                  <div key={key}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="inline-flex items-center gap-1.5">
                        {label}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button type="button" className="no-print text-muted-foreground hover:text-foreground" aria-label={`About ${label}`}>
                              <Info className="h-3.5 w-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs text-xs">{tooltip}</TooltipContent>
                        </Tooltip>
                      </span>
                      <span className={`font-semibold ${TONE_TEXT[tone]}`}>{val}/100</span>
                    </div>
                    <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-muted">
                      <div className={`h-full rounded-full ${TONE_BAR[tone]}`} style={{ width: `${val}%` }} />
                    </div>
                  </div>
                );
              })}

              {/* Approval Probability */}
              <div className="pt-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="inline-flex items-center gap-1.5">
                    Approval Probability
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button type="button" className="no-print text-muted-foreground hover:text-foreground" aria-label="About Approval Probability">
                          <Info className="h-3.5 w-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs text-xs">
                        Aggregated view of 214(b) risk signals: prior refusals, family in the US, English comfort, and applicant age.
                      </TooltipContent>
                    </Tooltip>
                  </span>
                  <span className={`font-semibold ${TONE_TEXT[approvalTone]}`}>
                    {approvalProbability}/100 · {riskLabel === "high" ? "High Risk" : riskLabel === "medium" ? "Moderate Risk" : "Low Risk"}
                  </span>
                </div>
                <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div className={`h-full rounded-full ${TONE_BAR[approvalTone]}`} style={{ width: `${approvalProbability}%` }} />
                </div>
              </div>
            </div>

            {scores.flags.length > 0 && (
              <div className="mt-5">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Red flags</p>
                <ul className="mt-2 space-y-1 text-sm">
                  {scores.flags.map((f) => (
                    <li key={f} className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-destructive" />{f}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Visual profile analytics */}
          <ProfileAnalytics form={form} scores={scores} riskLabel={riskLabel} />

          {/* Section cards */}
          <SectionCard title="Academic" data={ai.academic} />
          <SectionCard title="Financial" data={ai.financial} />
          <SectionCard title="Career" data={ai.career} />
          <SectionCard title="DS-160 consistency" data={ai.ds160Consistency} />
          <SectionCard title="Documentation" data={ai.documentation} />

          {/* Risk */}
          <div className="report-card mt-4 rounded-2xl border bg-card p-6 print-avoid-break">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-semibold">Consulate Refusal Risk Level</h2>
              <span className={`rounded-full px-3 py-1 text-xs font-medium ${TONE_BADGE_BG[toneFor(scores.risk >= 75 ? 100 : scores.risk >= 50 ? 60 : 30)]} ${TONE_TEXT[toneFor(scores.risk >= 75 ? 100 : scores.risk >= 50 ? 60 : 30)]}`}>
                {riskLabel.toUpperCase()} risk
              </span>
            </div>
            <p className="mt-2 text-sm text-foreground/80">{ai.riskAssessment.headline}</p>
            {ai.riskAssessment.consulateNotes && (
              <p className="mt-2 text-sm text-foreground/70"><b>Consulate notes:</b> {ai.riskAssessment.consulateNotes}</p>
            )}
            <TwoCols strengths={ai.riskAssessment.strengths} weaknesses={ai.riskAssessment.weaknesses} />
            <RecommendationsList items={ai.riskAssessment.recommendations} />
          </div>

          {/* Predicted interview questions */}
          <div className="report-card mt-4 rounded-2xl border bg-card p-6 print-avoid-break">
            <h2 className="font-display text-xl font-semibold">Predicted interview questions</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Tailored to your profile. Each entry shows a <em>strategic focus framework</em>, not a scripted answer.
            </p>
            <ol className="mt-4 space-y-4">
              {ai.interviewQuestions.slice(0, 5).map((q, i) => (
                <li key={i} className="rounded-lg border bg-background p-3">
                  <p className="font-medium">{i + 1}. {q.question}</p>
                  <p className="mt-1 text-xs text-muted-foreground"><b>Why it's risky:</b> {q.whyItsRisky}</p>
                  <p className="mt-2 text-sm"><b>Strategic focus:</b> {q.suggestedApproach}</p>
                </li>
              ))}
            </ol>

            <div className="mt-4 rounded-lg border-l-4 border-[#ca8a04] bg-[#fefce8] p-3 text-sm text-[#713f12]">
              <p className="flex items-start gap-2">
                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  <b>Consular Officers penalize generic or rehearsed scripts</b> - especially for repeat applicants.
                  Practice your non-scripted verbal delivery, tone, and body language live with a former visa specialist.
                </span>
              </p>
            </div>
            <div className="no-print mt-3">
              <Button asChild size="sm">
                <Link to="/pricing">Practice These Questions in a Live Mock Session →</Link>
              </Button>
            </div>
          </div>

          {/* Checklist */}
          <div className="report-card mt-4 rounded-2xl border bg-card p-6 print-avoid-break">
            <h2 className="font-display text-xl font-semibold">Personalized prep checklist</h2>
            <ul className="mt-3 space-y-2 text-sm">
              {ai.prepChecklist.map((c) => (
                <li key={c} className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />{c}</li>
              ))}
            </ul>
          </div>

          {/* Conditional Human Prep Package */}
          <PackageCard isRefused={isRefused} />

          {/* Print-only footer note */}
          <div className="print-only mt-6 border-t border-gray-300 pt-3 text-[10px] text-gray-600">
            WeLivdIt Confidential Profile Assessment · {site.legalName} · {site.email} · {site.phone}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}

// -------------------- Analytics module --------------------
function ProfileAnalytics({
  form,
  scores,
  riskLabel,
}: {
  form: PartialForm;
  scores: ScoreBreakdown;
  riskLabel: "low" | "medium" | "high";
}) {
  const consulate = String(form.s1?.targetConsulate ?? "your target consulate");
  const scrutinyMap: Record<string, { label: "High" | "Moderate" | "Low"; value: number; note: string }> = {
    Mumbai: { label: "High", value: 82, note: "Historically strict on 214(b) re-applications and family-in-US ties." },
    Delhi: { label: "High", value: 78, note: "Rigorous financial and DS-160 consistency checks." },
    Chennai: { label: "Moderate", value: 60, note: "Balanced approach, emphasises academic + course-relevance narrative." },
    Hyderabad: { label: "Moderate", value: 62, note: "Larger volume; watches for cloned SOP-style answers." },
    Kolkata: { label: "Low", value: 45, note: "Comparatively lenient; still probes funding coverage carefully." },
  };
  const scrutiny = scrutinyMap[consulate] ?? { label: "Moderate" as const, value: 60, note: "Set a target consulate to see specific trends." };

  // Financial coverage
  const totalCostUsd = Number(form.s5?.totalCostUsd ?? 0);
  const totalCostInr = totalCostUsd * 84;
  const loan = Number(form.s5?.loanAmountInr ?? 0);
  const liquid = Number(form.s5?.liquidFundsInr ?? 0);
  const sponsorIncome = Number(form.s5?.sponsorAnnualIncomeInr ?? 0);
  const totalFunds = loan + liquid + sponsorIncome * 2;
  const coveragePct = totalCostInr > 0 ? Math.min(200, Math.round((totalFunds / totalCostInr) * 100)) : 0;
  const debtRatio = sponsorIncome > 0 ? Math.round((loan / sponsorIncome) * 10) / 10 : 0; // years-of-income equivalent
  const debtTone: Tone = debtRatio > 5 ? "red" : debtRatio > 3 ? "yellow" : "green";

  // Benchmark matrix
  const gpa10 =
    form.s2?.gpa != null && form.s2.gpaScale
      ? form.s2.gpaScale === "4"
        ? (Number(form.s2.gpa) / 4) * 10
        : Number(form.s2.gpa)
      : 0;
  const gre = Number(String(form.s2?.greScore ?? "").replace(/[^0-9.]/g, ""));
  const benchmarks = [
    { label: "GPA (out of 10)", you: Math.round(gpa10 * 10), peer: 78, unit: "" },
    { label: "GRE (approx.)", you: gre >= 260 && gre <= 340 ? Math.round(((gre - 260) / 80) * 100) : 0, peer: 70, unit: gre ? ` · ${gre}` : "" },
    { label: "Financial coverage", you: Math.min(100, coveragePct), peer: 90, unit: "" },
    { label: "Home-country ties", you: scores.documentation, peer: 80, unit: "" },
    { label: "DS-160 clarity", you: scores.consistency, peer: 85, unit: "" },
  ];

  return (
    <div className="report-card mt-4 rounded-2xl border bg-card p-6 print-avoid-break">
      <div className="flex items-center gap-2">
        <Target className="h-5 w-5 text-primary" />
        <h2 className="font-display text-xl font-semibold">Official Profile Analytics & Insights</h2>
      </div>

      {/* Consulate scrutiny */}
      <div className="mt-4 rounded-xl border bg-background p-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">{consulate} Consulate Scrutiny Index</p>
          <span className={`text-xs font-semibold ${TONE_TEXT[scrutiny.value >= 75 ? "red" : scrutiny.value >= 50 ? "yellow" : "green"]}`}>
            {scrutiny.label} ({scrutiny.value}/100)
          </span>
        </div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={`h-full rounded-full ${TONE_BAR[scrutiny.value >= 75 ? "red" : scrutiny.value >= 50 ? "yellow" : "green"]}`}
            style={{ width: `${scrutiny.value}%` }}
          />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{scrutiny.note}</p>
        {riskLabel === "high" && (
          <p className="mt-1 text-xs text-[#b91c1c]">Combined with your profile flags, expect deeper follow-up probing.</p>
        )}
      </div>

      {/* Financial coverage & debt ratio */}
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <div className="rounded-xl border bg-background p-4">
          <p className="text-sm font-medium">Financial Coverage</p>
          <p className="mt-1 text-xs text-muted-foreground">
            First-year cost <b>${totalCostUsd.toLocaleString()}</b> ≈ ₹{Math.round(totalCostInr / 100000)}L
            <br />vs. declared funds ₹{Math.round(totalFunds / 100000)}L
          </p>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={`h-full rounded-full ${TONE_BAR[coveragePct >= 100 ? "green" : coveragePct >= 60 ? "yellow" : "red"]}`}
              style={{ width: `${Math.min(100, coveragePct)}%` }}
            />
          </div>
          <p className={`mt-1 text-xs font-medium ${TONE_TEXT[coveragePct >= 100 ? "green" : coveragePct >= 60 ? "yellow" : "red"]}`}>
            {coveragePct}% covered
          </p>
        </div>
        <div className="rounded-xl border bg-background p-4">
          <p className="text-sm font-medium">Debt-to-Sponsor-Income</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Loan ₹{Math.round(loan / 100000)}L vs. sponsor income ₹{Math.round(sponsorIncome / 100000)}L / yr
          </p>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
            <div className={`h-full rounded-full ${TONE_BAR[debtTone]}`} style={{ width: `${Math.min(100, debtRatio * 15)}%` }} />
          </div>
          <p className={`mt-1 text-xs font-medium ${TONE_TEXT[debtTone]}`}>
            {debtRatio > 0 ? `${debtRatio}× annual sponsor income` : "Add sponsor income + loan to see this."}
          </p>
        </div>
      </div>

      {/* Benchmark matrix */}
      <div className="mt-4 rounded-xl border bg-background p-4">
        <p className="text-sm font-medium">Profile Strength vs. Approved Peers</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Grey bar = median approved F-1 profile for a similar university/consulate. Coloured bar = you.
        </p>
        <div className="mt-3 space-y-3">
          {benchmarks.map((b) => {
            const tone = toneFor(b.you);
            return (
              <div key={b.label}>
                <div className="flex justify-between text-xs">
                  <span>{b.label}{b.unit}</span>
                  <span className={`font-medium ${TONE_TEXT[tone]}`}>{b.you}</span>
                </div>
                <div className="relative mt-1 h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div className="absolute inset-y-0 left-0 rounded-full bg-foreground/25" style={{ width: `${b.peer}%` }} />
                  <div className={`absolute inset-y-0 left-0 rounded-full ${TONE_BAR[tone]} mix-blend-multiply`} style={{ width: `${b.you}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// -------------------- Package card --------------------
function PackageCard({ isRefused }: { isRefused: boolean }) {
  if (isRefused) {
    return (
      <div className="report-card mt-6 rounded-2xl border-2 border-[#dc2626]/40 bg-gradient-to-br from-[#fff1f2] to-[#fef2f2] p-6 print-avoid-break">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-[#b91c1c]" />
          <p className="text-xs font-semibold uppercase tracking-widest text-[#b91c1c]">Recommended for you</p>
        </div>
        <h2 className="mt-2 font-display text-2xl font-semibold">Refusal Recovery Plan · ₹7,000</h2>
        <ul className="mt-4 space-y-2 text-sm">
          <li className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#dc2626]" /> 2× 1-on-1 Live Mock Sessions - Deep Refusal Analysis + Walk-in Final Mock</li>
          <li className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#dc2626]" /> Root Cause 214(b) Audit - pinpoints exactly why previous attempts failed</li>
          <li className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#dc2626]" /> "What Has Changed?" Strategic Defense Scripting</li>
          <li className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#dc2626]" /> Priority WhatsApp Support directly with a Senior Specialist until your interview date</li>
        </ul>
        <div className="no-print mt-5">
          <Button asChild className="w-full sm:w-auto">
            <Link to="/refusal-support">Get Refusal Recovery Package (₹7,000)</Link>
          </Button>
        </div>
        <div className="mt-4 text-xs">
          <Link to="/pricing" className="underline underline-offset-2 text-muted-foreground hover:text-foreground">
            Want to explore all our mock & DS-160 packages? View All Pricing Plans →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="report-card mt-6 rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-background p-6 print-avoid-break">
      <div className="flex items-center gap-2">
        <BookOpen className="h-5 w-5 text-primary" />
        <p className="text-xs font-semibold uppercase tracking-widest text-primary">Recommended for you</p>
      </div>
      <h2 className="mt-2 font-display text-2xl font-semibold">Standard Live Mock Prep · ₹4,500</h2>
      <ul className="mt-4 space-y-2 text-sm">
        <li className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" /> 1-on-1 Live Mock Interview with a Senior Specialist</li>
        <li className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" /> Complete DS-160 Verification & Cross-Audit</li>
        <li className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" /> Verbal Tone & Non-Scripted Framework Coaching</li>
      </ul>
      <div className="no-print mt-5">
        <Button asChild className="w-full sm:w-auto">
          <Link to="/full-support">Book Live Mock Session (₹4,500)</Link>
        </Button>
      </div>
      <div className="mt-4 text-xs">
        <Link to="/pricing" className="underline underline-offset-2 text-muted-foreground hover:text-foreground">
          Want to explore all our mock & DS-160 packages? View All Pricing Plans →
        </Link>
      </div>
    </div>
  );
}

// -------------------- Reusable pieces --------------------
function SectionCard({ title, data }: { title: string; data: AiAnalysis["academic"] }) {
  return (
    <div className="report-card mt-4 rounded-2xl border bg-card p-6 print-avoid-break">
      <h2 className="font-display text-xl font-semibold">{title}</h2>
      <p className="mt-2 text-sm text-foreground/80">{data.headline}</p>
      <TwoCols strengths={data.strengths} weaknesses={data.weaknesses} />
      <RecommendationsList items={data.recommendations} />
    </div>
  );
}

function TwoCols({ strengths, weaknesses }: { strengths: string[]; weaknesses: string[] }) {
  return (
    <div className="mt-4 grid gap-4 md:grid-cols-2">
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-primary">Strengths</p>
        <ul className="mt-2 space-y-1 text-sm">
          {strengths.map((s) => <li key={s} className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />{s}</li>)}
        </ul>
      </div>
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-destructive">Weaknesses</p>
        <ul className="mt-2 space-y-1 text-sm">
          {weaknesses.map((s) => <li key={s} className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-destructive" />{s}</li>)}
        </ul>
      </div>
    </div>
  );
}

function RecommendationsList({ items }: { items: string[] }) {
  return (
    <div className="mt-4">
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Recommendations</p>
      <ul className="mt-2 space-y-1 text-sm">
        {items.map((i) => <li key={i} className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-foreground/50" />{i}</li>)}
      </ul>
    </div>
  );
}
